import { format, differenceInMinutes, addHours } from "date-fns";
import { cn } from "@/lib/utils";
import { WorkOrder, Tractor, Location } from "@/types/tms";
import { Wrench } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface WorkOrderCardProps {
  workOrder: WorkOrder;
  tractor: (Tractor & { home_base: Location | null }) | null;
  weekStart: Date;
  totalMinutes: number;
}

export function WorkOrderCard({
  workOrder,
  tractor,
  weekStart,
  totalMinutes,
}: WorkOrderCardProps) {
  const startTime = new Date(workOrder.start_time);
  const endTime = addHours(startTime, workOrder.duration_hours);

  // Calculate position and width as percentages
  const startMinutes = differenceInMinutes(startTime, weekStart);
  const durationMinutes = Math.max(differenceInMinutes(endTime, startTime), 60);

  const leftPercent = Math.max(0, (startMinutes / totalMinutes) * 100);
  const widthPercent = Math.min((durationMinutes / totalMinutes) * 100, 100 - leftPercent);

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "absolute top-2 h-[calc(100%-16px)] rounded hover:ring-2 hover:ring-primary/50 transition-shadow",
              "order-card-work-order"
            )}
            style={{
              left: `${leftPercent}%`,
              width: `${Math.max(widthPercent, 0.3)}%`,
              minWidth: "4px",
            }}
          />
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1.5 text-xs">
            <div className="font-semibold flex items-center gap-1.5">
              <Wrench className="h-3 w-3 text-[hsl(var(--status-in-transit))]" />
              {workOrder.unique_id}
              <span className="text-[hsl(var(--status-in-transit))]">(Work Order)</span>
            </div>
            {workOrder.description && (
              <div className="text-muted-foreground">
                {workOrder.description}
              </div>
            )}
            {tractor?.home_base && (
              <div className="text-muted-foreground">
                Return to: {tractor.home_base.city || tractor.home_base.short_code}
              </div>
            )}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1 border-t">
              <div>
                <span className="text-muted-foreground">Start:</span>{" "}
                {format(startTime, "MMM d, HH:mm")}
              </div>
              <div>
                <span className="text-muted-foreground">End:</span>{" "}
                {format(endTime, "MMM d, HH:mm")}
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
